import React from "react";
import { RotateCcw, X } from "lucide-react";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { useAppSelector } from "@src/store/hooks";
import helperStyles from "@src/components/helpers.module.scss";
import { clx } from "../utils/stringUtils";
import { formatCurrency } from "../utils/numberUtils";
import { splitTags } from "./bulkTag.utils";
import styles from "./BulkTag.module.scss";

type TBulkTagItem = {
	data: EventInventoryRecord;
	newTags: Array<string>;
	removedTags: Array<string>;
	onToggleExistingTag?: (listingId: number, tag: string) => void;
	onRemove?: (listingId: number) => void;
};

const BulkTagItem = ({ data, newTags, removedTags, onToggleExistingTag, onRemove }: TBulkTagItem) => {
	const eventName = useAppSelector((state) => state.pricing.selection.event?.Event_Headliner ?? "");

	const existingTags = splitTags(data.Tags);
	const keptTags = existingTags.filter((x) => !removedTags.includes(x));
	const addedTags = newTags.filter((x) => !keptTags.includes(x));

	return (
		<div className={styles.singleListing}>
			<div className={styles.secRowSeat}>
				<div className={styles.secRowSeatCont}>
					<div style={{ userSelect: "none" }} className={clx(styles.itemTextPrimary, helperStyles.ellipsisDiv)}>
						{eventName}
					</div>
					<div className={styles.itemTextPrimary}>
						{data.Section}, Row {data.Row}
					</div>
					<div style={{ gap: "2px 8px" }} className={clx(styles.textSecondary, helperStyles.flexSmallGap)}>
						<div>ID: {data.Listing_ID}</div>
						<div>Seat {data.Seats}</div>
					</div>
				</div>
			</div>
			<div className={styles.tagsColumn}>
				{existingTags.length || addedTags.length ? (
					<div className={styles.rowTags}>
						{existingTags.map((x) => {
							const isRemoved = removedTags.includes(x);
							return (
								<span key={x} data-removed={isRemoved} className={styles.rowTag}>
									<span className={styles.rowTagLabel}>{x}</span>
									<span
										className={styles.rowTagAction}
										data-disabled={!onToggleExistingTag}
										title={isRemoved ? "Keep tag" : "Remove tag"}
										onClick={() => onToggleExistingTag?.(data.Listing_ID, x)}>
										{isRemoved ? <RotateCcw size={10} /> : <X size={12} />}
									</span>
								</span>
							);
						})}
						{addedTags.map((x) => (
							<span key={`new-${x}`} data-new="true" className={styles.rowTag}>
								<span className={styles.rowTagLabel}>+ {x}</span>
							</span>
						))}
					</div>
				) : (
					"-"
				)}
			</div>
			<div className={clx(styles.qty, helperStyles.ellipsisDiv)}>{data.Quantity || 1}</div>
			<div className={clx(styles.priceLabel, helperStyles.ellipsisDiv)}>{formatCurrency(data.Item_Price ?? 0)}</div>
			<div onClick={() => onRemove?.(data.Listing_ID)} className={styles.xContainer} data-disabled={!onRemove}>
				<div>
					<X size={16} />
				</div>
			</div>
		</div>
	);
};

export default BulkTagItem;
